import { useEffect, useRef, useState } from "react";
import { useSpring } from "react-spring";
import useMeasure from "react-use-measure";

import { useIsomorphicLayoutEffect } from "@shared/hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect";

/**
 * @param accordionState - the initial state of the accordion
 * @param skipAnimation {*} - used in MenuItemAccordion to prevent the accordion from re-animating when remounted (from sidebar expansion)
 */
const useAccordionAnimation = (accordionState: boolean, skipAnimation?: boolean) => {
  const [isExpanded, setIsExpanded] = useState(accordionState);
  const [isAnimating, setIsAnimating] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    if (accordionState !== isExpanded) {
      setIsExpanded(accordionState);
    }
  }, [accordionState]);

  const [ref, { height: viewHeight }] = useMeasure();
  const { height, opacity, y } = useSpring({
    from: {
      height: 0,
      opacity: 0,
      y: 0,
    },
    to: {
      height: isExpanded ? viewHeight : 0,
      opacity: isExpanded ? 1 : 0,
      y: isExpanded ? 0 : 10,
    },
    onStart: {
      height: () => setIsAnimating(true),
    },
    onRest: () => {
      if (mounted.current) setIsAnimating(false);
    },
    immediate: skipAnimation,
  });

  useIsomorphicLayoutEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  return { setIsExpanded, isExpanded, isAnimating, ref, height, opacity, y };
};

export { useAccordionAnimation };
